<script setup lang="ts">
import { computed } from 'vue';
import SchoolClassEntityTab from 'src/modules/School/components/SchoolClassEntityTab/SchoolClassEntityTab.vue';
import type {
  ClassCourseProgressRow,
  CourseOption,
} from 'src/modules/School/composables/useSchoolData';

const props = withDefaults(
  defineProps<{
    assignedCourses: CourseOption[];
    totalAssignedCourses: number;
    search: string;
    isExpanded: boolean;
    selectedCourseDocumentId: string;
    selectedCourseName?: string;
    progressRows: ClassCourseProgressRow[];
    isProgressLoading: boolean;
    /** False in a locked semester: unassigning stays available, assigning does not. */
    canAdd?: boolean;
    /** Show the per-course "Preview" action (teacher walkthrough). */
    showPreview?: boolean;
    previewLoadingDocumentId?: string;
  }>(),
  {
    canAdd: true,
    selectedCourseName: undefined,
    showPreview: false,
    previewLoadingDocumentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:search', value: string): void;
  (e: 'toggle-expanded'): void;
  (e: 'select-course', courseDocumentId: string): void;
  (e: 'preview-course', courseDocumentId: string): void;
  (
    e: 'set-student-assignment',
    payload: { studentDocumentId: string; assigned: boolean },
  ): void;
}>();

const entities = computed(() =>
  props.assignedCourses.map((course) => ({
    documentId: course.documentId,
    name: course.course_name,
    assignedCount: course.assignedCount,
    completedCount: course.completedCount,
  })),
);

const rows = computed(() =>
  props.progressRows.map((row) => ({
    student: row.student,
    enrollmentStatus: row.enrollmentStatus,
    assigned: row.assigned,
    userProgress: row.userCourse,
  })),
);
</script>

<template>
  <SchoolClassEntityTab
    :entities="entities"
    :total-assigned="totalAssignedCourses"
    :search="search"
    :is-expanded="isExpanded"
    :selected-document-id="selectedCourseDocumentId"
    :selected-name="selectedCourseName"
    :progress-rows="rows"
    :is-progress-loading="isProgressLoading"
    :can-add="canAdd"
    :show-preview="showPreview"
    :preview-loading-document-id="previewLoadingDocumentId"
    title-key="COURSES"
    selected-fallback-key="SCHOOL_SELECTED_COURSE"
    select-prompt-key="SCHOOL_SELECT_ASSIGNED_COURSE_TO_VIEW_PROGRESS"
    @update:search="emit('update:search', $event)"
    @toggle-expanded="emit('toggle-expanded')"
    @select="emit('select-course', $event)"
    @preview="emit('preview-course', $event)"
    @set-student-assignment="emit('set-student-assignment', $event)"
  />
</template>
